import i18next from '@/shared/i18n';
import { Toast } from 'antd-mobile';

export * from './typeGuards';

function getDayOfWeek(dateString: string): string {
  const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土'];
  const date = new Date(
    `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}`,
  );
  const dayOfWeek = daysOfWeek[date.getDay()];
  return `${dayOfWeek}`;
}

// formatDate 20230926 -> 9月26日 (月)
function formatDate(dateString: string): string {
  const month = parseInt(dateString.slice(4, 6), 10);
  const day = parseInt(dateString.slice(6, 8), 10);
  const dayOfWeek = getDayOfWeek(dateString);
  return `${month}月${day}日 (${dayOfWeek})`;
}

function formatDiscountType({
  type,
  amountPer,
  discountPrice,
  discountRate,
  activityContent,
}: {
  type: number;
  amountPer?: number;
  discountPrice?: number;
  discountRate?: number;
  activityContent?: string;
}): {
  typeTitle: string;
  typeDescription: string;
} {
  if (type === 1) {
    return {
      typeTitle: '割引（%）',
      typeDescription: `${discountRate}%OFF割引`,
    };
  }
  if (type === 2) {
    return {
      typeTitle: '割引（円）',
      typeDescription: `${amountPer}円で${discountPrice}円OFF`,
    };
  }
  if (type === 3) {
    return {
      typeTitle: '無料/プレゼント',
      typeDescription: `${activityContent}`,
    };
  }
  return {
    typeTitle: '',
    typeDescription: '',
  };
}

// 千分位分隔符 1000 -> 1,000
function formatPoint(value: number | undefined) {
  if (typeof value === 'undefined' || Number.isNaN(Number(value))) return '0';

  try {
    return new Intl.NumberFormat().format(Number(value));
  } catch (e) {
    return '0';
  }
}

// 校验邮箱
function validateEmail(email: string): { valid: boolean; message: string } {
  const reg =
    /[\w!#$%&'*+/=?^_`{|}~-]+(?:\.[\w!#$%&'*+/=?^_`{|}~-]+)*@(?:[\w](?:[\w-]*[\w])?\.)+[\w](?:[\w-]*[\w])?/;
  return {
    valid: reg.test(email),
    message: '有効なメールアドレスを入力してください。',
  };
}

/*
 * @description: 全角转半角
 * @param {string} str
 * @return {string}
 * @example
 * toHalfWidth('１２３') => '123'
 * toHalfWidth('１２３abc') => '123abc'
 */
function toHalfWidth(str: string): string {
  try {
    return str.replace(/[０-９]/g, (ch) => {
      return String.fromCharCode(ch.charCodeAt(0) - 0xfee0);
    });
  } catch (error) {
    console.error('Error converting full-width to half-width:', error);
    // 返回原始字符串，防止异常影响功能
    return str;
  }
}

// 半角转全角
function toFullWidth(str: string): string {
  return str.replace(/[0-9-]/g, (ch) => {
    if (ch === '-') {
      // U+FF0D 全角短横
      return '－';
    }
    // 半角数字 '0'(0x30) → 全角数字 '０'(0xFF10)
    return String.fromCharCode(ch.charCodeAt(0) + 0xff10 - 0x30);
  });
}

// 校验日本手机号，0? 代表日本手机号码前面的 0 可有可无
function validateJapanesePhoneNumber(phoneNumber: string): readonly [boolean, string] {
  const reg = /^0?[789]0\d{8}$/;
  return [reg.test(phoneNumber), i18next.t('wallet:form.enterValidJapanesePhoneNumber')] as const;
}

// 自动插入ハイフン：支持携帯(070/080/090)与固定電話(0X)的格式化
// 携帯: 070/080/090-XXXX-XXXX（最大 11 桁）
// 固定: 0X-XXXX-XXXX（最大 10 桁；本项目按「区号2桁」简化）
function formatJapanesePhoneNumber(input: string): string {
  if (!input) return '';
  // 1. 统一为半角数字并去除非数字
  let digits = String(input)
    .replace(/[－ー―‐]/g, '-')
    .replace(/\D/g, '')
    .replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));

  // 2. 判断类型
  // 允许省略首位 0 的输入（例如：70xxxxxxxx → 自动补 0 → 070-xxxx-xxxx）
  const missingZeroMobile = /^(70|80|90)\d{8}$/.test(digits);
  if (missingZeroMobile) {
    digits = `0${digits}`;
  }
  const isMobilePrefix = /^(070|080|090)/.test(digits);
  const is050 = /^050/.test(digits);
  const is0570 = /^0570/.test(digits);
  const isLandline2Prefix = /^0\d/.test(digits) && !isMobilePrefix && !is050 && !is0570; // 0X 开头但非携帯、050、0570

  if (isMobilePrefix) {
    // 070/080/090-XXXX-XXXX
    digits = digits.slice(0, 11);
    const p1 = digits.slice(0, 3);
    const p2 = digits.slice(3, 7);
    const p3 = digits.slice(7);
    if (digits.length <= 3) return p1;
    if (digits.length <= 7) return `${p1}-${p2}`;
    return `${p1}-${p2}-${p3}`;
  }

  if (is050) {
    // 050-XXXX-XXXX（VoIP）
    digits = digits.slice(0, 11);
    const p1 = digits.slice(0, 3);
    const p2 = digits.slice(3, 7);
    const p3 = digits.slice(7);
    if (digits.length <= 3) return p1;
    if (digits.length <= 7) return `${p1}-${p2}`;
    return `${p1}-${p2}-${p3}`;
  }

  if (is0570) {
    // 0570-XXXX-XXXX（ナビダイヤル風：4-4-4）
    digits = digits.slice(0, 12);
    const p1 = digits.slice(0, 4);
    const p2 = digits.slice(4, 8);
    const p3 = digits.slice(8);
    if (digits.length <= 4) return p1;
    if (digits.length <= 8) return `${p1}-${p2}`;
    return `${p1}-${p2}-${p3}`;
  }

  if (isLandline2Prefix) {
    // 0X-XXXX-XXXX（如 03/06）
    digits = digits.slice(0, 10);
    const p1 = digits.slice(0, 2);
    const p2 = digits.slice(2, 6);
    const p3 = digits.slice(6);
    if (digits.length <= 2) return p1;
    if (digits.length <= 6) return `${p1}-${p2}`;
    return `${p1}-${p2}-${p3}`;
  }

  // 3. 兜底：未知前缀时按 2-4-其余 分段（最大 10 桁）
  digits = digits.slice(0, 10);
  const p1 = digits.slice(0, 2);
  const p2 = digits.slice(2, 6);
  const p3 = digits.slice(6);
  if (digits.length <= 2) return p1;
  if (digits.length <= 6) return `${p1}-${p2}`;
  return `${p1}-${p2}-${p3}`;
}

// 同时支持携帯(070/080/090)与固定電話(0X)的带ハイフン校验
// 要求：
//  - 携帯: 070/080/090-XXXX-XXXX（ハイフン2つ必須）
//  - 固定: 0X-XXXX-XXXX（市外局番2桁 + 8桁、ハイフン2つ必須）
//  - 入力中のハイフンは保持（正規表現はハイフン込みで検証）
function validateJapanesePhoneWithHyphen(
  input: string,
): readonly [boolean, string, 'mobile' | 'landline' | undefined] {
  const value = String(input || '')
    // 统一全角/长音符等横线为半角ハイフン
    .replace(/[－ー―‐]/g, '-')
    // 去除空格
    .replace(/\s+/g, '')
    // 转半角数字
    .replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));

  // 允许省略首位 0 的携帯输入：70/80/90 也视为有效
  const mobileReg = /^(0?70|0?80|0?90)-\d{4}-\d{4}$/;
  // 固定電話/その他サービス: 03/06等(2-4-4)、050(3-4-4)、0570(4-4-4)
  const landlineReg = /^(?:0\d-\d{4}-\d{4}|050-\d{4}-\d{4}|0570-\d{4}-\d{4})$/;

  if (mobileReg.test(value)) return [true, '', 'mobile'] as const;
  if (landlineReg.test(value)) return [true, '', 'landline'] as const;

  // 根据前缀给出更具体的错误提示
  const startsLikeMobile = /^(070|080|090)/.test(value);
  const startsLike050 = /^050/.test(value);
  const startsLike0570 = /^0570/.test(value);
  const startsLikeLandline = /^0\d/.test(value);

  if (startsLikeMobile) {
    return [false, '携帯番号は 080-1234-5678 の形式で入力してください。', undefined] as const;
  }
  if (startsLike050) {
    return [false, '050-1234-5678 の形式で入力してください。', undefined] as const;
  }
  if (startsLike0570) {
    return [false, '0570-1234-5678 の形式で入力してください。', undefined] as const;
  }
  if (startsLikeLandline) {
    return [false, '固定電話は 03-1234-5678 の形式で入力してください。', undefined] as const;
  }

  return [false, '電話番号の形式が正しくありません。', undefined] as const;
}

// 检测当前设备的操作系统 const os = detectMobileOS();
function detectMobileOS(): { isAndroid: boolean; isIOS: boolean } {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isAndroid = /android/i.test(userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  return { isAndroid, isIOS };
}

// 唤起 App
const os = detectMobileOS();

// ios 测试环境下先尝试唤起本机已安装的应用，如果没有则唤起 TestFlight
function openInDevelopment(code: string) {
  const appScheme = `wasai://web.wasaiapp.com/wpaInvitation?code=${code}`;
  const testFlightScheme = 'itms-beta://';

  if (!code) {
    window.location.href = testFlightScheme;
    return;
  }

  // 是否已经唤起 App
  let appLaunched = false;

  // 尝试通过 App Scheme 唤起 App
  window.location.href = appScheme;

  // 监听页面是否变为不可见，作为应用被唤起的标志
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      appLaunched = true;
    }
  });

  // 如果在一定时间内没有唤起 App，则跳唤起 TestFlight
  setTimeout(() => {
    if (!appLaunched) {
      window.location.href = testFlightScheme;
    }
  }, 2000);
}

// 生成安卓唤起链接
function generateAndroidIntentLink(code, pathTo) {
  // 固定的前缀和后缀
  const HOST = 'web.wasaiapp.com';
  const URL_PREFIX = `intent://${HOST}/wpaInvitation?`;
  const URL_SUFFIX = '#Intent;scheme=wasai;package=com.smartiful.wasai;end';

  // 构造查询参数
  const params = new URLSearchParams();
  if (code) {
    params.set('code', code);
  }
  if (pathTo) {
    params.set('path', pathTo);
  }
  const queryString = params.toString();

  // 回退链接：如果应用未安装，则跳转到 Google Play 商店
  const fallbackUrl = encodeURIComponent(
    'https://play.google.com/store/apps/details?id=com.smartiful.wasai',
  );

  // 如果已有查询参数，则用 & 连接，否则直接添加回退参数
  const separator = queryString ? '&' : '';

  // 拼接最终的 intent 链接
  return `${URL_PREFIX}${queryString}${separator}S.browser_fallback_url=${fallbackUrl}${URL_SUFFIX}`;
}

function launchApp(code: string | undefined, pathTo?: string) {
  console.log(os);

  // 如果是测试环境的 iOS，唤起 TestFlight openInDevelopment(code || '');
  const environment = import.meta.env.VITE_ENV;
  if (os.isIOS && environment === 'development') {
    openInDevelopment(code || '');
    return;
  }

  // 生成安卓唤起链接
  const _androidLink = generateAndroidIntentLink(code, pathTo);
  console.log('_androidLink', _androidLink);

  // 深度链接;
  const deepLink = os.isAndroid
    ? _androidLink
    : `wasai://web.wasaiapp.com/wpaInvitation?code=${code}`;

  // 应用商店链接
  const appStoreLink = os.isAndroid
    ? 'https://play.google.com/store/apps/details?id=com.smartiful.wasai'
    : 'https://apps.apple.com/jp/app/wasai/id6463698487';

  // 如果没有 code，则是视频分享
  if (!code) {
    if (os.isAndroid) {
      // 尝试通过按钮唤起视频播放页
      const HOST = 'web.wasaiapp.com';
      const URL_PREFIX = `https://${HOST}/share?url=`;
      const URL_SUFFIX = '&path=video';
      console.log(`${URL_PREFIX}MTE0NTcxMTc0Mjk1NDQzOTM4MA=${URL_SUFFIX}`);

      window.location.href = `${URL_PREFIX}MTE0NTcxMTc0Mjk1NDQzOTM4MA=${URL_SUFFIX}`;
    } else {
      // 苹果的话打开商店
      window.location.href = appStoreLink;
    }
    return;
  }

  // 是否已经唤起 App
  let appLaunched = false;

  // 尝试通过深度链接唤起 App
  window.location.href = deepLink;

  // 监听页面是否变为不可见，作为应用被唤起的标志
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      appLaunched = true;
    }
  });

  console.log('deepLink', deepLink);

  // 如果在一定时间内没有唤起 App，则跳转到应用商店
  setTimeout(() => {
    if (!appLaunched) {
      window.location.href = appStoreLink;
    }
  }, 2000);
}

function appendShareLog(...args: any[]) {
  try {
    const el = document.getElementById('share-log');
    if (!el) return;
    const s = args
      .map((a) => {
        try {
          return typeof a === 'string' ? a : JSON.stringify(a);
        } catch {
          return String(a);
        }
      })
      .join(' ');
    const div = document.createElement('div');
    div.textContent = s;
    el.appendChild(div);
  } catch (e) {
    console.error(e);
  }
}

function launchAppByUrlOrWasaiId(args: { url?: string; wasaiId?: string }) {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isAndroid = /Android/i.test(ua);
  const isIOSUA = /iPad|iPhone|iPod/i.test(ua);
  const isiPadOS13 = navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints) > 1;
  const isIOS = (isIOSUA || isiPadOS13) && !(window as any).MSStream;
  const environment = import.meta.env.VITE_ENV;
  const { url, wasaiId } = args || {};
  if (isIOS) {
    const WEB_URL = import.meta.env.VITE_WEB_URL || '';
    const HOST = WEB_URL.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    let universalLink = '';
    if (url && !wasaiId) {
      universalLink = `wasai://${HOST}/share?url=${encodeURIComponent(url)}`;
    } else if (wasaiId && !url) {
      universalLink = `wasai://${HOST}/shareProfile?wasaiId=${encodeURIComponent(wasaiId)}`;
    } else {
      const appStoreLink = 'https://apps.apple.com/jp/app/wasai/id6463698487';
      const testFlightScheme = 'itms-beta://';
      if (environment === 'development') {
        console.log('share-log:testFlightScheme', testFlightScheme);
        appendShareLog('share-log:testFlightScheme', testFlightScheme);
        window.location.href = testFlightScheme;
      } else {
        console.log('share-log:ios appStoreLink', appStoreLink);
        appendShareLog('share-log:ios appStoreLink', appStoreLink);
        window.location.href = appStoreLink;
      }
      return;
    }
    let appLaunched = false;
    console.log('share-log:universalLink ios', universalLink);
    appendShareLog('share-log:universalLink ios', universalLink);
    window.location.href = universalLink;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        appLaunched = true;
      }
    });
    setTimeout(() => {
      console.log('share-log:appLaunched ios', appLaunched);
      appendShareLog('share-log:appLaunched ios', appLaunched);
      if (!appLaunched) {
        if (environment === 'development') {
          const testFlightScheme = 'itms-beta://';
          console.log('share-log:testFlightScheme', testFlightScheme);
          appendShareLog('share-log:testFlightScheme', testFlightScheme);
          window.location.href = testFlightScheme;
        } else {
          const appStoreLink = 'https://apps.apple.com/jp/app/wasai/id6463698487';
          console.log('share-log:ios fallbackUrl', appStoreLink);
          appendShareLog('share-log:ios fallbackUrl', appStoreLink);
          window.location.href = appStoreLink;
        }
      }
    }, 2000);
    return;
  }
  console.log('isAndroid', isAndroid);
  if (isAndroid) {
    const WEB_URL = import.meta.env.VITE_WEB_URL || '';
    const HOST = WEB_URL.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    const PACKAGE =
      environment === 'development' ? 'com.smartiful.wasai.dev' : 'com.smartiful.wasai';
    const fallbackUrl = `https://play.google.com/store/apps/details?id=${PACKAGE}`;
    let deepLink = '';
    if (url && !wasaiId) {
      const qs = new URLSearchParams();
      qs.set('url', url);
      const queryString = qs.toString();
      deepLink = `wasai://${HOST}/share?${queryString}`;
    } else if (wasaiId && !url) {
      const params = new URLSearchParams();
      params.set('wasaiId', wasaiId);
      const queryString = params.toString();
      deepLink = `wasai://${HOST}/shareProfile?${queryString}`;
    } else {
      console.log('share-log:url and wasaiId are empty');
      appendShareLog('share-log:url and wasaiId are empty');
      window.location.href = fallbackUrl;
      return;
    }
    let appLaunched = false;
    console.log('share-log:deepLink android', deepLink);
    appendShareLog('share-log:deepLink android', deepLink);
    window.location.href = deepLink;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        appLaunched = true;
      }
    });
    setTimeout(() => {
      console.log('share-log:appLaunched android', appLaunched);
      appendShareLog('share-log:appLaunched android', appLaunched);
      if (!appLaunched) {
        console.log('share-log:fallbackUrl', fallbackUrl);
        appendShareLog('share-log:fallbackUrl', fallbackUrl);
        window.location.href = fallbackUrl;
      }
    }, 2000);
  }
}

// 解析原生日期选择器的返回，兼容字符串或 { code, data }
function getDateFromBridgeResponse(res: any): string | null {
  if (typeof res === 'string') {
    return res || null;
  }
  if (res && typeof res === 'object') {
    const { code, data } = res as any;
    if (code === 200 && typeof data === 'string' && data) return data;
  }
  return null;
}

/*
 * @description: 校验输入框
 * @param {string} field
 * @param {string | number} value
 * @param {string} helperText
 * @return {Promise<[string, boolean, string]>}
 * @example
 * validateInput('title', '123', '请输入标题') => ['title', false, '']
 * validateInput('title', '', '请输入标题') => ['title', true, '请输入标题']
 */
function validateInput(
  field: string,
  value: string | number,
  helperText: string,
): Promise<[string, boolean, string]> {
  return new Promise((resolve) => {
    if (!value) {
      resolve([field, true, helperText]);
    } else {
      resolve([field, false, '']);
    }
  });
}

async function handleCopy(text?: string) {
  const value = text || '';
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(String(value));
      Toast.show(i18next.t('common:copySuccess'));
    } catch (err) {
      Toast.show(i18next.t('common:copyFailed'));
      console.error('复制失败: ', err);
    }
  } else {
    // 创建备用复制方案
    const textarea = document.createElement('textarea');
    textarea.value = String(value);
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      Toast.show(i18next.t('common:copySuccess'));
    } catch (err) {
      Toast.show(i18next.t('common:copyFailed'));
      console.error('复制失败: ', err);
    }
    document.body.removeChild(textarea);
  }
}

// 将全角日文数字转为半角数字，例如：１２３ -> 123
function convertJapaneseNumbersToHalfWidth(value: string): string {
  try {
    return value.replace(/[０-９]/g, (ch) => {
      return String.fromCharCode(ch.charCodeAt(0) - 0xfee0);
    });
  } catch (error) {
    console.error('Error converting Japanese numbers to half-width:', error);
    // 返回原始字符串，防止异常影响功能
    return value;
  }
}

// 漢字、平仮名、片仮名、英字のみ、々
export const kanjiHiraganaKatakanaEnglishRegex =
  /^[\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FFA-Za-z々ヶ]+$/;

// 片仮名のみ
export const katakanaRegex = /^[\u30A0-\u30FF々ヶ]+$/;

// 街道地址等输入框 汉字・日文平假名片假名・长音符・空格・全角数字・全角连字符
export const streetAddressRegex = /^[一-龯ぁ-んァ-ンー・\s０-９－々ヶ]+$/;

// 校验是否为 undefined
function stripUndefined<T>(input: T): T {
  return JSON.parse(JSON.stringify(input));
}

export {
  getDayOfWeek,
  formatDate,
  formatDiscountType,
  formatPoint,
  validateEmail,
  detectMobileOS,
  launchApp,
  launchAppByUrlOrWasaiId,
  validateInput,
  validateJapanesePhoneNumber,
  validateJapanesePhoneWithHyphen,
  toHalfWidth,
  toFullWidth,
  handleCopy,
  convertJapaneseNumbersToHalfWidth,
  stripUndefined,
  formatJapanesePhoneNumber,
  getDateFromBridgeResponse,
};
